// app/src/lib/scale/webhooks.js — outbound webhook subscriptions (US-5.8).
//
// The client surface is admin-only: create / disable / list subscriptions.
// Actual delivery happens server-side in the `webhook-deliver` Lambda which:
//   - signs each payload with HMAC-SHA256 (secret stored in Secrets Manager),
//   - retries up to 5 times with exponential backoff,
//   - dead-letters failed deliveries to SQS,
//   - records each attempt in `webhookDeliveries` so the user can view status.
//
// Subscription secrets are minted server-side on creation; the secret is shown
// to the user exactly once on the response and never stored client-side.

import { db } from '../db.js';
import { currentClaims } from '../auth/cognitoClient.js';
import { getConfig } from '../config.js';
import { getAccessToken } from '../auth/cognitoClient.js';
import { gate } from './members.js';
import * as audit from './audit.js';

const VALID_EVENTS = new Set([
  'invoice.created', 'invoice.confirmed',
  'price_alert.fired',
  'order.submitted',
  'cash_gap.detected',
]);

export async function create(input) {
  const claims = await currentClaims();
  if (!claims) throw new Error('webhook: not signed in');
  await gate(claims.sub, 'webhook.manage');
  if (!input.url || !/^https:\/\//.test(input.url)) throw new Error('webhook: HTTPS url required');
  if (!Array.isArray(input.events) || input.events.length === 0) throw new Error('webhook: events required');
  for (const e of input.events) if (!VALID_EVENTS.has(e)) throw new Error(`webhook: unknown event "${e}"`);

  const cfg = getConfig();
  const at = await getAccessToken();
  const res = await fetch(`${cfg.apiBaseUrl}/webhooks`, {
    method: 'POST',
    headers: { authorization: `Bearer ${at}`, 'content-type': 'application/json' },
    body: JSON.stringify({ url: input.url, events: input.events, description: input.description }),
  });
  if (!res.ok) throw new Error(`webhook: create ${res.status}`);
  const body = await res.json();

  // Persist the subscription locally for the management UI (secret is NOT stored).
  const sub = {
    subscriptionId: body.subscriptionId,
    ownerId: claims.sub,
    url: input.url,
    events: input.events,
    description: input.description,
    status: 'active',
    createdAt: Date.now(),
  };
  await db.webhookSubscriptions.put(sub);
  await audit.record({ action: 'create', entityType: 'webhookSubscription', entityId: sub.subscriptionId, after: { url: input.url, events: input.events } });
  return { subscription: sub, secret: body.secret };
}

export async function disable(subscriptionId) {
  const claims = await currentClaims();
  if (!claims) throw new Error('webhook: not signed in');
  await gate(claims.sub, 'webhook.manage');
  const sub = await db.webhookSubscriptions.get(subscriptionId);
  if (!sub || sub.ownerId !== claims.sub) throw new Error('webhook: not found');

  const cfg = getConfig();
  const at = await getAccessToken();
  const res = await fetch(`${cfg.apiBaseUrl}/webhooks/${encodeURIComponent(subscriptionId)}`, {
    method: 'DELETE',
    headers: { authorization: `Bearer ${at}` },
  });
  if (!res.ok) throw new Error(`webhook: disable ${res.status}`);
  sub.status = 'disabled';
  await db.webhookSubscriptions.put(sub);
  await audit.record({ action: 'delete', entityType: 'webhookSubscription', entityId: subscriptionId });
}

export async function list() {
  const claims = await currentClaims();
  if (!claims) return [];
  return db.webhookSubscriptions.where('ownerId').equals(claims.sub).toArray();
}

export async function recentDeliveries(subscriptionId, limit = 50) {
  return db.webhookDeliveries
    .where('subscriptionId').equals(subscriptionId)
    .reverse()
    .limit(limit)
    .toArray();
}
